import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import {SentimentDissatisfied, SentimentNeutral, SentimentSatisfiedAlt} from "@mui/icons-material";
import SuggestionGroup from "../components/suggestions/SuggestionGroup";
import {Article_Type_Enum, GetRecommendedProductsQuery, useGetRecommendedProductsQuery,} from "../generated/graphql";
import {useTranslation} from "react-i18next";
import {countryCodeFromLanguage} from "../translations/i18n";
import {useSelector} from "react-redux";
import {RootState} from "../store/store.ts";
import {QuestionnaireType} from "../data/AnswerEnums.ts";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useState} from "react";
import {submitFeedback} from "../store/questionnaireSlice.ts";
import {useStoreDispatch} from "../store/hooks.ts";
import {theme} from "../theme.ts";


const ARTICLE_TYPE_ORDER: `${Article_Type_Enum}`[] = [
    "bike",  // bike-profile
    "saddle", // pelvis
    "handle", // hand
    "innerbarends", // hand
    "handlebars",  // hand
    "handlebar_tape",
    "stem",  // hand
    "pedal", // pedal
    "insole", // pedal
    "gloves",  // hand
    "jersey", // clothing
    "shorts", // pelvis, clothing
    "pants",  // pelvis, clothing
    "clothing", // clothing
    "accessory",
    "shoes",  // pedal
    "helmet",
    "elbow_pads",
    "knee_pads",
    "socks" // pedal
];

export type Recommendation = GetRecommendedProductsQuery['recommendation_own_articles'][0];

interface ResetDialogState {
    show: boolean;
}

interface FeedbackDialogState {
    open: boolean;
    selectedFeedback: "" | "happy" | "neutral" | "sad";
    feedbackText: string;
}

export default function Results() {
    const {t, i18n} = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useStoreDispatch();


    const questionnaireId = useSelector((state: RootState) => state.questionnaire.questionnaireId);
    const countryCode = countryCodeFromLanguage(i18n.language);
    const {data, loading, error} = useGetRecommendedProductsQuery({variables: {answer_id: questionnaireId!, language: countryCode}, fetchPolicy: 'network-only'});

    const [resetDialog, setResetDialog] = useState<ResetDialogState>({ show: false });
    const [feedbackDialog, setFeedbackDialog] = useState<FeedbackDialogState>({ open: false, selectedFeedback: "", feedbackText: "" });
    const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
    const [isSending, setIsSending] = useState(false);



    const recommendations: Recommendation[] = data?.recommendation_own_articles ?? [];
    const questionnaireTypeParam = searchParams.get("questionnaireType");
    const questionnaireTypesParam = searchParams.get("questionnaireTypes");
    
    // Parse questionnaire types from URL parameters
    let questionnaireTypes: QuestionnaireType[] = [];
    if (questionnaireTypesParam) {
        // Handle multiple questionnaire types
        questionnaireTypes = questionnaireTypesParam.split(',').map(type => type as QuestionnaireType);
    } else if (questionnaireTypeParam) {
        // Handle single questionnaire type
        questionnaireTypes = [questionnaireTypeParam as QuestionnaireType];
    }

    const filtered_query_ARTICLE_TYPE_ORDER = ARTICLE_TYPE_ORDER.filter((articleType) => {
        if(questionnaireTypes.length > 0){
            // Check if any of the selected questionnaire types match the article type
            return questionnaireTypes.some(questionnaireType => {
                // If whole is selected, show all article types
                if (questionnaireType === QuestionnaireType.whole) {
                    return true;
                }
                // Special handling for clothing questionnaire type
                if (questionnaireType === QuestionnaireType.clothing){
                    return articleType === "clothing" || articleType === "pants" || articleType === "shorts" || articleType === "socks" || articleType === "gloves"  || articleType === "jersey" ;
                }
                if (questionnaireType === QuestionnaireType.handle) {
                    return articleType === QuestionnaireType.handle || articleType === "handlebar_tape"
                }
                // For other questionnaire types, match directly
                return questionnaireType === articleType;
            });
        } else {
            return true;
        }
    });

    const groupedData: Record<string, Recommendation[]> = {};

    filtered_query_ARTICLE_TYPE_ORDER.forEach((type) => {
        const articlesWithType = recommendations.filter((recommendation: Recommendation) => {
            return recommendation.article_informaton_i18n[0].article_type === type;
        });
        if (articlesWithType.length !== 0) {
            groupedData[type] = articlesWithType;
        }
    });

    const handleResetClick = () => {
        setResetDialog({ show: true });
    };

    const handleResetConfirm = () => {
        setResetDialog({ show: false });
        navigate('/');
    };

    const handleResetCancel = () => {
        setResetDialog({ show: false });
    };

    const handleFeedbackClick = () => {

        setFeedbackDialog({ ...feedbackDialog, open: true });
    };

    const handleFeedbackClose = () => {
        setFeedbackDialog({ ...feedbackDialog, open: false });
    };

    const getFeedbackRating = (feedback: string): string => {
        switch (feedback) {
            case "happy":
                return "good";
            case "neutral":
                return "neutral";
            case "sad":
                return "bad";
            default:
                return "";
        }
    };

    const handleFeedbackSend = async () => {
        if (!feedbackDialog.selectedFeedback) {
            return;
        }

        setIsSending(true);

        try {
            await dispatch(
                submitFeedback({
                rating: getFeedbackRating(feedbackDialog.selectedFeedback),
                comment: feedbackDialog.feedbackText
            })).unwrap();

            setFeedbackSent(true);
            setFeedbackDialog({ open: false, selectedFeedback: "", feedbackText: "" });
        } catch (error) {
            console.error('Fehler beim Senden des Feedbacks:', error);
        } finally {
            setIsSending(false);
        }
    };

    const getCurrentColoring = (feedbackType: string): string => {
        switch (feedbackType) {
            case "happy":
                return  (feedbackDialog.selectedFeedback === feedbackType || feedbackDialog.selectedFeedback === "" ) ? "green" : "grey";
            case "neutral":
                return  (feedbackDialog.selectedFeedback === feedbackType || feedbackDialog.selectedFeedback === "" ) ? "orange" : "grey";
            case "sad":
                return  (feedbackDialog.selectedFeedback === feedbackType || feedbackDialog.selectedFeedback === "" ) ? "red" : "grey";
            default:
                return "grey";
        }
    };


    if (loading) return <div>{t("loading")}</div>;
    if (error) {
        console.log('GraphQL Fehler', error);
        return <div>{t("error_occurred")} {error.message}</div>;
    }

    return (
        <Container disableGutters className="" sx={{
            flexGrow: 1,
            textAlign: "center",
            padding: 0,
            maxWidth: "100%", // Stellt sicher, dass der Container die volle Breite einnimmt

        }}>
            <Box sx={{
                mb: 2,
            }}>
                <Button
                    variant="contained"
                    onClick={handleResetClick}
                    sx={{
                        position: 'absolute',
                        right: 15,
                        top: 20,
                        fontSize: theme.typography.body1.fontSize,
                    }}
                >
                    {t("reset")}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleFeedbackClick}
                    disabled={feedbackSent}
                    sx={{
                        position: 'absolute',
                        left: 15,
                        fontSize: theme.typography.body1.fontSize,
                        top: 20
                    }}
                >
                    {t("feedback_button")}
                </Button>
            </Box>
            <div style={{
                position: 'relative',
                marginBottom: "20px",
                paddingTop: "8px"
            }}>
                <Typography variant="h4" sx={{ margin: "auto" }}>
                    {t("questionnaire_finish_headline")}
                </Typography>
            </div>

            {/* Reset Warning Dialog */}
            <Dialog
                open={resetDialog.show}
                onClose={handleResetCancel}
                aria-labelledby="reset-dialog-title"
                aria-describedby="reset-dialog-description"
            >
                <DialogTitle id="reset-dialog-title" fontSize={theme.typography.body1.fontSize}>
                    {t("reset_warning_title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="reset-dialog-description" fontSize={theme.typography.body1.fontSize}>
                        {t("reset_warning_content")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions >
                    <Button onClick={handleResetCancel}>
                        {t("reset_cancel")}
                    </Button>
                    <Button onClick={handleResetConfirm} autoFocus color="primary" variant="contained" sx={{fontSize: theme.typography.body1.fontSize}}>
                        {t("reset_confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Feedback Dialog */}
            <Dialog
                open={feedbackDialog.open}
                onClose={handleFeedbackClose}
                aria-labelledby="feedback-dialog-title"
            >
                <DialogTitle id="feedback-dialog-title"  fontSize={theme.typography.body1.fontSize}>
                    {t("feedback_dialog_title")}
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" justifyContent="space-around" mb={2}>
                        <IconButton
                            onClick={() => setFeedbackDialog({...feedbackDialog, selectedFeedback: "happy"})}
                            sx={{
                                background: feedbackDialog.selectedFeedback === "happy" ? "rgba(0, 0, 0, 0.1)" : "transparent",
                                fontSize: theme.typography.body1.fontSize
                            }}
                        >
                            <SentimentSatisfiedAlt sx={{ color: getCurrentColoring("happy") }}/>
                        </IconButton>
                        <IconButton
                            onClick={() => setFeedbackDialog({...feedbackDialog, selectedFeedback: "neutral"})}
                            sx={{
                                background: feedbackDialog.selectedFeedback === "neutral" ? "rgba(0, 0, 0, 0.1)" : "transparent",
                                fontSize: theme.typography.body1.fontSize
                            }}
                        >
                            <SentimentNeutral sx={{ color: getCurrentColoring("neutral") }}/>
                        </IconButton>
                        <IconButton
                            onClick={() => setFeedbackDialog({...feedbackDialog, selectedFeedback: "sad"})}
                            sx={{
                                background: feedbackDialog.selectedFeedback === "sad" ? "rgba(0, 0, 0, 0.1)" : "transparent",
                                fontSize: theme.typography.body1.fontSize
                            }}
                        >
                            <SentimentDissatisfied sx={{ color: getCurrentColoring("sad") }}/>
                        </IconButton>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder={t("feedback_dialog_placeholder")}
                        value={feedbackDialog.feedbackText}
                        onChange={(e) => setFeedbackDialog({...feedbackDialog, feedbackText: e.target.value})}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleFeedbackClose}
                        color="primary"
                    >
                        {t("feedback_dialog_cancel")}
                    </Button>
                    <Button
                        onClick={handleFeedbackSend}
                        color="primary"
                        variant="contained"
                        disabled={!feedbackDialog.selectedFeedback || isSending}
                    >
                        {isSending ? t("sending") : t("feedback_dialog_send")}
                    </Button>
                </DialogActions>
            </Dialog>
            {Object.entries(groupedData).map(([key, value]) => (
                <SuggestionGroup key={key} title={key} items={value} />
            ))}
            <footer style={{color: "lightgrey"}}>{t("copyright_notice")}</footer>
        </Container>
    );
}
